import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { formatPrice } from "@/lib/format";
import api, { apiError } from "@/lib/api";

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "District of Columbia", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois",
  "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts",
  "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
  "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota",
  "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
  "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming",
];

const EMPTY = {
  name: "", email: "", phone: "",
  s_full_name: "", s_line1: "", s_line2: "", s_city: "", s_state: "", s_postal: "",
  shipping: "standard",
};

const Field = ({ label, id, className = "", ...props }) => (
  <div className={className}>
    <Label htmlFor={id} className="overline text-muted-foreground">{label}</Label>
    <Input id={id} className="rounded-none mt-1.5" data-testid={`req-${id}`} {...props} />
  </div>
);

export default function Cart() {
  const { items, updateQuantity, removeItem, subtotal, clearCart } = useCart();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const es = lang === "es";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      items: items.map((i) => ({
        product_id: i.product_id, name: i.name, image: i.image, price: i.price,
        quantity: i.quantity, size: i.size, color: i.color,
      })),
      customer: { name: form.name, email: form.email, phone: form.phone },
      shipping_address: {
        full_name: form.s_full_name, line1: form.s_line1, line2: form.s_line2,
        city: form.s_city, state: form.s_state, postal_code: form.s_postal,
        country: "United States", phone: form.phone,
      },
      shipping_method: form.shipping,
      payment_method: "request",
    };
    try {
      const { data } = await api.post("/orders", payload);
      clearCart();
      setOpen(false);
      setForm(EMPTY);
      navigate(`/order/${data.order.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-center px-5" data-testid="empty-cart">
        <div>
          <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground mb-6" strokeWidth={1} />
          <h1 className="font-serif text-4xl mb-3">{t("Your bag is empty")}</h1>
          <p className="text-muted-foreground mb-8">{t("Discover pieces crafted to be treasured.")}</p>
          <Link to="/shop"><Button className="rounded-none h-12 px-8" data-testid="cart-shop-btn">{t("Explore the Collection")}</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-12" data-testid="cart-page">
      <h1 className="font-serif text-5xl font-light mb-3">{es ? "Tu Selección" : "Your Selection"}</h1>
      <p className="text-muted-foreground mb-10 max-w-2xl">
        {es
          ? "Añade las piezas que te interesan y envía tu solicitud. Nuestro equipo de M&A Jewelry te contactará para coordinar tu pedido — no se realiza ningún pago en línea."
          : "Add the pieces you love and send your request. Our M&A Jewelry team will contact you to arrange your order — no online payment is taken."}
      </p>
      <div className="grid lg:grid-cols-3 gap-12">
        {/* Items */}
        <div className="lg:col-span-2">
          <div className="border-t border-border">
            {items.map((item) => (
              <div key={item.line_id} className="flex gap-5 py-6 border-b border-border" data-testid={`cart-item-${item.product_id}`}>
                <Link to={`/product/${item.slug || item.product_id}`} className="w-24 h-28 sm:w-28 sm:h-32 shrink-0 bg-secondary overflow-hidden">
                  <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-4">
                    <div>
                      <Link to={`/product/${item.slug || item.product_id}`}><h3 className="font-serif text-xl leading-tight hover:text-gold transition-colors">{item.name}</h3></Link>
                      <div className="text-xs text-muted-foreground mt-1 space-x-3">
                        {item.size && <span>{t("Size")}: {item.size}</span>}
                        {item.color && <span>{t("Finish")}: {item.color}</span>}
                      </div>
                    </div>
                    <button onClick={() => removeItem(item.line_id)} className="text-muted-foreground hover:text-destructive transition-colors h-fit" data-testid={`remove-item-${item.product_id}`}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border border-border">
                      <button className="h-9 w-9 grid place-items-center hover:bg-secondary transition-colors" onClick={() => updateQuantity(item.line_id, item.quantity - 1)} data-testid={`cart-qty-dec-${item.product_id}`}><Minus className="h-3.5 w-3.5" /></button>
                      <span className="w-9 text-center text-sm" data-testid={`cart-qty-${item.product_id}`}>{item.quantity}</span>
                      <button className="h-9 w-9 grid place-items-center hover:bg-secondary transition-colors" onClick={() => updateQuantity(item.line_id, item.quantity + 1)} data-testid={`cart-qty-inc-${item.product_id}`}><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <span className="font-medium">{formatPrice(item.price * item.quantity)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/shop" className="inline-flex items-center gap-2 text-sm link-underline mt-6">
            {t("Continue shopping")}
          </Link>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="bg-secondary/40 border border-border p-7 sticky top-32" data-testid="order-summary">
            <h2 className="font-serif text-2xl mb-6">{es ? "Resumen de tu solicitud" : "Request Summary"}</h2>

            <div className="space-y-3 text-sm border-t border-border pt-5">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{es ? "Piezas" : "Pieces"}</span>
                <span data-testid="summary-count">{items.reduce((n, i) => n + i.quantity, 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{es ? "Valor estimado" : "Estimated value"}</span>
                <span data-testid="summary-subtotal">{formatPrice(subtotal)}</span>
              </div>
            </div>

            <p className="text-xs text-muted-foreground mt-5 leading-relaxed">
              {es
                ? "El valor es solo de referencia. No se cobra en línea; el equipo confirmará disponibilidad y detalles de envío contigo."
                : "This value is for reference only. No online payment; the team will confirm availability and shipping details with you."}
            </p>

            <Button className="w-full rounded-none h-14 mt-6 group" onClick={() => setOpen(true)} data-testid="send-request-btn">
              <Send className="h-4 w-4 mr-2" /> {es ? "Enviar solicitud" : "Send Request"}
            </Button>
          </div>
        </div>
      </div>

      {/* Request dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl font-light">{es ? "Enviar solicitud" : "Send Request"}</DialogTitle>
            <DialogDescription>
              {es
                ? "Completa tus datos y dirección de envío. Solo realizamos envíos dentro de Estados Unidos."
                : "Fill in your details and shipping address. We only ship within the United States."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitRequest} className="space-y-8 mt-2">
            {/* Contact */}
            <section>
              <h3 className="overline text-gold mb-4">{es ? "Tus datos" : "Your details"}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={es ? "Nombre" : "Name"} id="name" required value={form.name} onChange={set("name")} />
                <Field label={es ? "Correo electrónico" : "Email"} id="email" type="email" required value={form.email} onChange={set("email")} />
                <Field label={es ? "Número de celular" : "Phone number"} id="phone" required value={form.phone} onChange={set("phone")} className="sm:col-span-2" />
              </div>
            </section>

            {/* Shipping address */}
            <section>
              <h3 className="overline text-gold mb-4">{es ? "Dirección de envío" : "Shipping address"}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={es ? "Nombre del destinatario" : "Recipient name"} id="s_full_name" required value={form.s_full_name} onChange={set("s_full_name")} className="sm:col-span-2" />
                <Field label={es ? "Dirección" : "Address"} id="s_line1" required value={form.s_line1} onChange={set("s_line1")} className="sm:col-span-2" />
                <Field label={es ? "Apartamento, suite (opcional)" : "Apartment, suite (optional)"} id="s_line2" value={form.s_line2} onChange={set("s_line2")} className="sm:col-span-2" />
                <Field label={es ? "Ciudad" : "City"} id="s_city" required value={form.s_city} onChange={set("s_city")} />
                <div>
                  <Label className="overline text-muted-foreground">{es ? "Estado / Región" : "State / Region"}</Label>
                  <Select value={form.s_state} onValueChange={(v) => setForm((f) => ({ ...f, s_state: v }))} required>
                    <SelectTrigger className="rounded-none mt-1.5" data-testid="req-s_state"><SelectValue placeholder={es ? "Selecciona un estado" : "Select a state"} /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {US_STATES.map((st) => (
                        <SelectItem key={st} value={st} data-testid={`state-${st}`}>{st}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Field label={es ? "Código postal" : "Postal code"} id="s_postal" required value={form.s_postal} onChange={set("s_postal")} />
                <div>
                  <Label className="overline text-muted-foreground">{es ? "País" : "Country"}</Label>
                  <Input value={es ? "Estados Unidos" : "United States"} disabled className="rounded-none mt-1.5" data-testid="req-country" />
                </div>
              </div>
            </section>

            {/* Shipping method */}
            <section>
              <h3 className="overline text-gold mb-4">{es ? "Tipo de envío" : "Shipping method"}</h3>
              <RadioGroup value={form.shipping} onValueChange={(v) => setForm((f) => ({ ...f, shipping: v }))} className="grid sm:grid-cols-2 gap-3">
                <label className={`flex items-center gap-3 border p-4 cursor-pointer transition-colors ${form.shipping === "standard" ? "border-gold" : "border-border"}`} data-testid="req-ship-standard">
                  <RadioGroupItem value="standard" />
                  <div>
                    <p className="text-sm font-medium">{es ? "Envío normal" : "Standard shipping"}</p>
                    <p className="text-xs text-muted-foreground">{es ? "3–5 días hábiles" : "3–5 business days"}</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 border p-4 cursor-pointer transition-colors ${form.shipping === "express" ? "border-gold" : "border-border"}`} data-testid="req-ship-express">
                  <RadioGroupItem value="express" />
                  <div>
                    <p className="text-sm font-medium">{es ? "Envío exprés" : "Express shipping"}</p>
                    <p className="text-xs text-muted-foreground">{es ? "1–2 días hábiles" : "1–2 business days"}</p>
                  </div>
                </label>
              </RadioGroup>
            </section>

            <Button type="submit" className="w-full rounded-none h-14" disabled={loading} data-testid="submit-request-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> {es ? "Enviar solicitud" : "Send Request"}</>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
